import { flat, map, pipe, toArray, toAsync } from '@fxts/core';
import axios from 'axios';
import { NewMakerPrintResultF } from '@modules/NewMaker/PrintResult/F/Function/module/NewMakerPrintResultF';
import { changeDpiBlob } from 'changedpi';

interface SvgStrokeOptions {
  precision?: number;
  tag?: string; // 스타일 적용할 태그명
  stroke?: string; // 근사색 예: "#FF00FF"
  strokeWidth?: string | number; // 예: 0.2 또는 "0.2mm"
  strokeDasharray?: string; // 예: "2,1"
}

const svgDownloadVersion: {
  [key: string]: {
    svg_style: SvgStrokeOptions;
  };
} = {
  ['v-bps-template-svg']: {
    /* 띠부 */
    svg_style: {
      tag: 'g',
      stroke: '#FF00FF',
      strokeWidth: '0.1mm',
      strokeDasharray: 'none',
    },
  },
};

export const needSvgProductPageFile = ({ template_meta } = {}) => {
  if (!template_meta) return;
  if (!Array.isArray(template_meta)) return;
  return template_meta?.some((tm) => svgDownloadVersion[tm.version]);
};

export const makeSvgProductPageFiles = async ({
  svg_product_id,
  up_c_s_id,
  template_meta,
  task_id,
  title,
}: {
  svg_product_id: number;
  up_c_s_id: number;
  task_id?: number;
  template_meta: object[];
  title: object;
}): Promise<{ file_name: string; canvas_blob: File; task_id: number; up_c_s_id: number }[]> => {
  return pipe(
    template_meta,
    map(async (tm) => {
      const svg_version = svgDownloadVersion[tm.version];
      const svg_style = svg_version.svg_style;
      const dpi = tm.dpi;
      const file_name = NewMakerPrintResultF.makeTitleByKeys(title, tm.text.file_name);
      const {
        data: { data: svg_product_pages },
      } = await axios.get<{ data: { printable_url: string; cutting_line_url: string }[] }>(
        `/@api/svg_products/${svg_product_id}`,
      );
      return await pipe(
        svg_product_pages,
        toAsync,
        map(async ({ printable_url, cutting_line_url }) => {
          const [{ data: printable }, { data: svg }] = await Promise.all([
            axios.get<ArrayBuffer>(printable_url, { responseType: 'arraybuffer' }),
            axios.get<ArrayBuffer>(cutting_line_url, { responseType: 'arraybuffer' }),
          ]);
          const blob = new Blob([new Uint8Array(printable)], { type: 'image/png' });
          return [
            {
              file_name: `${file_name.replace(/\s/, '_')}.png`,
              canvas_blob: await changeDpiBlob(blob, dpi),
              task_id,

              up_c_s_id,
            },
            {
              file_name: `${file_name.replace(/\s/, '_')}.svg`,
              canvas_blob: new Blob([new Uint8Array(convertSvgPx300dpiToMmWithStroke(svg, svg_style))], {
                type: 'image/svg+xml',
              }),
              task_id,
              up_c_s_id,
            },
          ];
        }),
        toArray,
        (arr) => {
          return arr.flat();
        },
      );
    }),
    toAsync,
    toArray,
    (arr) => {
      return arr.flat();
    },
  );
};

/**
 * width/height(px→mm, 300 dpi) 변환 + 특정 태그에 stroke 속성들 적용
 */
export function convertSvgPx300dpiToMmWithStroke(
  svgBuffer: Buffer | ArrayBuffer | Uint8Array,
  opts: SvgStrokeOptions = {},
): Buffer {
  const { precision = 2, tag, stroke, strokeWidth, strokeDasharray } = opts;

  const svgStr = Buffer.isBuffer(svgBuffer)
    ? svgBuffer.toString('utf8')
    : Buffer.from(svgBuffer as ArrayBuffer | Uint8Array).toString('utf8');

  // px 문자열을 mm 로 변환 (300 DPI 기준)
  const parseLen = (raw: string) => {
    const m = String(raw)
      .trim()
      .match(/^([+-]?\d*\.?\d+(?:[eE][+-]?\d+)?)([a-z%]*)$/i);
    if (!m) return { value: NaN, unit: '' };
    return { value: parseFloat(m[1]), unit: m[2].toLowerCase() };
  };
  const pxToMm300 = (px: number) => (px / 300) * 25.4;

  // width/height 속성 변환
  const replaceAttr = (input: string, attr: 'width' | 'height') => {
    const re = new RegExp(`(${attr}\\s*=\\s*["'])([^"']+)(["'])`, 'i');
    return input.replace(re, (full, pre, val, post) => {
      const { value, unit } = parseLen(val);
      if (isNaN(value)) return full;
      if (unit === 'mm') return full;
      if (unit && unit !== 'px') return full;
      const mm = pxToMm300(value);
      const mmStr = mm.toFixed(precision).replace(/\.?0+$/, '');
      return `${pre}${mmStr}mm${post}`;
    });
  };

  let out = svgStr;
  out = replaceAttr(out, 'width');
  out = replaceAttr(out, 'height');

  // stroke 속성 추가
  if (tag && (stroke || strokeWidth || strokeDasharray)) {
    const tagRegex = new RegExp(`<${tag}([^>]*)>`, 'gi');

    out = out.replace(tagRegex, (full, attrs) => {
      let updated = attrs;

      if (stroke) {
        if (/stroke\s*=/.test(updated))
          updated = updated.replace(/stroke\s*=\s*["'][^"']*["']/, `stroke="${stroke}"`);
        else updated += ` stroke="${stroke}"`;
      }

      if (strokeWidth) {
        const val = typeof strokeWidth === 'number' ? `${strokeWidth}mm` : strokeWidth;
        if (/stroke-width\s*=/.test(updated))
          updated = updated.replace(/stroke-width\s*=\s*["'][^"']*["']/, `stroke-width="${val}"`);
        else updated += ` stroke-width="${val}"`;
      }

      if (strokeDasharray) {
        if (/stroke-dasharray\s*=/.test(updated))
          updated = updated.replace(
            /stroke-dasharray\s*=\s*["'][^"']*["']/,
            `stroke-dasharray="${strokeDasharray}"`,
          );
        else updated += ` stroke-dasharray="${strokeDasharray}"`;
      }

      return `<${tag}${updated}>`;
    });
  }

  return Buffer.from(out, 'utf8');
}
